import { isPlainObject } from 'lodash-es';
import { ChartType, ConfigChartType } from '@dbl/core/chart';
import type { TChartType } from '@dbl/core/chart';
import { NULL_UI_DEFAULT, normalizeDataLabelConfig } from '@dbl/core/chartEditor';
import type {
  IAnalysisLineChartConfig,
  IBuildRightChartConfigParams,
  IChartConfigSource,
} from './types';

/** DVA 无对应图表配置时的空对象，避免解构/取值报错 */
const EMPTY_SOURCE: IChartConfigSource = {};

/** 图表切换未配置时的默认值 */
const DEFAULT_CHART_SWITCH: NonNullable<IAnalysisLineChartConfig['chartSwitch']> = {
  show: false,
  chartTypes: [],
};
/**
 * 字段说明（description）格式归一化。
 * 新数据为 { 字段名: 文案 }；历史数据为 { 字段名: { desc: 文案 } }，需转成图表/表格组件可用的结构。
 */
function normalizeDescription(
  raw: IChartConfigSource['description'],
): Record<string, string> {
  if (!raw || !isPlainObject(raw)) {
    return {};
  }
  return Object.keys(raw).reduce<Record<string, string>>((acc, key) => {
    const entry = raw[key];
    if (typeof entry === 'string') {
      acc[key] = entry;
    } else if (isPlainObject(entry) && typeof entry.desc === 'string') {
      acc[key] = entry.desc;
    }
    return acc;
  }, {});
}

/**
 * 解析表格列宽持久化 JSON（saveWidth / tempData.saveWidth）。
 * 解析失败或格式异常时返回 undefined，不阻断页面渲染。
 */
function safeParseTableColumnWidths(json?: string): number[] | undefined {
  try {
    const parsed = JSON.parse(json || '[]');
    if (!Array.isArray(parsed)) {
      return undefined;
    }
    const widths = parsed.filter((item): item is number => typeof item === 'number');
    return widths.length > 0 ? widths : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 从 DVA save_column_infos 读取当前图表类型的 customizeSetting 切片。
 * storageKey 由 ConfigChartType 决定（如 Line → lineChart，StackBar → stackChart）。
 */
function readDvaSource(
  saveColumnInfos: IBuildRightChartConfigParams['saveColumnInfos'],
  chartType: TChartType,
): IChartConfigSource {
  const storageKey = ConfigChartType[chartType];
  if (!storageKey) {
    return EMPTY_SOURCE;
  }
  return (
    saveColumnInfos?.chartsCfgSave?.[chartType]?.customizeSetting?.[storageKey]
    ?? EMPTY_SOURCE
  );
}

/**
 * 解析图表样式配置来源：优先 Context BASE，否则回退 DVA 切片
 */
function resolveConfigSource(
  params: IBuildRightChartConfigParams,
): IChartConfigSource {
  const { chartType, baseFromContext, saveColumnInfos } = params;
  if (Object.keys(baseFromContext).length > 0) {
    return baseFromContext as IChartConfigSource;
  }
  return readDvaSource(saveColumnInfos, chartType);
}

/**
 * 合并表格交互态：列宽、排序、分页。
 * 优先 tempData（用户改表头/分页未点保存前的临时态），与旧版 useMemo 行为一致。
 */
function applyTablePersistState(
  config: IAnalysisLineChartConfig,
  saveColumnInfos?: IBuildRightChartConfigParams['saveColumnInfos'],
): void {
  const widths = safeParseTableColumnWidths(saveColumnInfos?.saveWidth);
  if (widths) {
    config.tableColumnWidths = widths;
  }
  if (saveColumnInfos?.sorts) {
    config.sorts = saveColumnInfos.sorts;
  }
  if (saveColumnInfos?.savePaginAtion) {
    config.pageSize = saveColumnInfos.savePaginAtion;
  }

  const temp = saveColumnInfos?.tempData;
  if (!temp) {
    return;
  }
  if (temp.savePaginAtion) {
    config.pageSize = temp.savePaginAtion;
  }
  if (temp.sorts) {
    config.sorts = temp.sorts;
  }
  const tempWidths = safeParseTableColumnWidths(temp.saveWidth);
  if (tempWidths) {
    config.tableColumnWidths = tempWidths;
  }
}

/**
 * 构建分析页主图表区使用的 chartConfig（rightChartConfig）。
 *
 * 数据标签在入口统一 normalize（旧 showValues → 新字段）；图表组件只消费新字段。
 * showValues / showValuesIndexs 仍透传 DVA 切片（历史计算结果），供非渲染链路只读。
 */
export function buildRightChartConfig(
  params: IBuildRightChartConfigParams,
): IAnalysisLineChartConfig {
  const { chartType, saveColumnInfos } = params;
  const dvaSource = readDvaSource(saveColumnInfos, chartType);
  const source = resolveConfigSource(params);

  const showValues = dvaSource.showValues ?? 0;
  const showValuesIndexs = dvaSource.showValuesIndexs ?? [0];

  const dataLabel = normalizeDataLabelConfig(
    {
      showValues,
      showDataLabel: source.showDataLabel,
      displayForm: source.displayForm,
      labelContent: source.labelContent,
    },
    chartType,
  );

  const config: IAnalysisLineChartConfig = {
    showValues,
    showValuesIndexs,
    yAxisName: source.yAxisName ?? '',
    yAxisRangeSwitch: source.yAxisRangeSwitch ?? false,
    yAxisRangeSwitch2: source.yAxisRangeSwitch2 ?? false,
    fieldSetting: source.field_setting ?? {},
    description: normalizeDescription(source.description),
    referenceLine: source.reference_line ?? [],
    mapColors: source.map_colors,
    xLabel: source.x_label,
    colorSetting: source.colorSetting,
    conditionalFormat: source.conditional_format,
    freeze: source.freeze,
    mergeSameCells: source.merge_same_cells,
    column_sort: Array.isArray(source.column_sort)
      ? (source.column_sort as IAnalysisLineChartConfig['column_sort'])
      : undefined,
    xAxisName: source.xAxisName,
    showDataLabel: dataLabel.showDataLabel,
    displayForm: dataLabel.displayForm,
    labelContent: dataLabel.labelContent,
    tooltipSetting: source.tooltipSetting,
    stackedDynamicState: source.stackedDynamicState,
    legendPosition: source.legend_position,
    legendColors: source.legend_colors,
    chartSwitch: source.chart_switch ?? DEFAULT_CHART_SWITCH,
    dualYAxisEnabled: source.dualYAxisEnabled,
    primaryYAxis: source.primaryYAxis,
    secondYAxis: source.secondYAxis,
    nullUi: source.null_ui ?? NULL_UI_DEFAULT,
  };

  applyTablePersistState(config, saveColumnInfos);
  return config;
}

